import { Fragment, useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Redirect, Route, useHistory } from 'react-router-dom';
import {
  IonToast,
  IonTabs,
  IonTabBar,
  IonTabButton,
  IonLabel,
  IonLoading,
  IonRouterOutlet,
  IonButton,
  getPlatforms,
  createGesture
} from '@ionic/react';
import { ScreenOrientation } from '@ionic-native/screen-orientation';
import { StatusBar, Style } from '@capacitor/status-bar';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ReactComponent as StoryIcon } from 'assets/tab-icons/story.svg';
import { ReactComponent as WorkoutIcon } from 'assets/tab-icons/workouts.svg';
import { ReactComponent as FuelingPlanIcon } from 'assets/tab-icons/fueling-plan.svg';
import { ReactComponent as FileExplorerIcon } from 'assets/tab-icons/file-explorer.svg';
import { ReactComponent as BarwisIcon } from 'assets/tab-icons/barwis.svg';
import Menu from './components/Menu';
import BottomDrawer from './components/BottomDrawer';

import layoutStore from 'stores/layout.store';
import barwisFileStore from 'stores/barwis-file.store';
import userStore from 'stores/user.store';
import { useThemeLogoIcon } from 'libs/theme/useTheme';

import classes from './layout.module.scss';

// Pages
import DashboardPage from 'pages/dashboard';
import StoryPage from 'pages/story';
import WorkoutsPage from 'pages/workouts';
import WorkoutSinglePage from 'pages/workouts/workout-single';
import ExerciseSinglePage from 'pages/workouts/exercise-single';
import FuelingPlanPage from 'pages/fueling-plan';
import EducationalResourcePage from 'pages/educational-resources';
import EducationalResourceTagPage from 'pages/educational-resources/tag';
import EducationalResourceSinglePage from 'pages/educational-resources/educational-resource-single';
import FileExplorerPage from 'pages/file-explorer';
import ServicePage from 'pages/services';
import FeedbackPage from 'pages/feedback';
import HelpPage from 'pages/help';
import ReadinessProgressPage from 'pages/reports/readiness-progress';
import Auth from 'pages/auth';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin
);

const Layout = ({ children }) => {
  const downloadLinkRef = useRef(null);
  const homeButtonRef = useRef();
  const { push } = useHistory();
  const [isBottomDrawerOpen, setIsBottomDrawerOpen] = useState(false);
  const toastConfig = layoutStore.getToastConfig();
  const isAuthenticated = userStore.getIsAuthenticated();
  const isAuthenticating = userStore.getIsAuthenticating();
  const isLoggingOut = userStore.getIsLoggingOut();
  const isFileLoading = barwisFileStore.getIsFileLoading();
  const logoIconUrl = useThemeLogoIcon();
  let lastOnClick = 0;

  //App initialization
  useEffect(() => {
    const runningPlatforms = new Set(getPlatforms());
    if (runningPlatforms.has('capacitor') || runningPlatforms.has('cordova')) {
      layoutStore.setPlatform('mobile');
      ScreenOrientation.lock(ScreenOrientation.ORIENTATIONS.PORTRAIT);
      StatusBar.setStyle({ style: Style.Dark });
    } else {
      layoutStore.setPlatform('web');
    }
    layoutStore.setDownloadRef(downloadLinkRef);
  }, []);

  useEffect(() => {
    const DOUBLE_CLICK_THRESHOLD = 300;
    if (homeButtonRef && homeButtonRef.current) {
      let timer = null;
      const gesture = createGesture({
        el: homeButtonRef.current,
        gestureName: 'doubleClick',
        threshold: 0,
        onStart: () => {
          const now = Date.now();
          if (Math.abs(now - lastOnClick) <= DOUBLE_CLICK_THRESHOLD) {
            lastOnClick = 0;
            clearTimeout(timer);
            push('/dashboard');
          } else {
            lastOnClick = now;
            timer = setTimeout(() => {
              lastOnClick = 0;
              setIsBottomDrawerOpen(true);
            }, DOUBLE_CLICK_THRESHOLD);
          }
        }
      });
      gesture.enable();
      console.log('gesture enabled!');
    }
  }, [homeButtonRef.current]);

  return (
    <Fragment>
      {children}
      {isAuthenticated ? (
        <Fragment>
          <svg
            className={classes.tabBackgroundSvg}
            xmlns='http://www.w3.org/2000/svg'
            xmlnsXlink='http://www.w3.org/1999/xlink'
            viewBox='0 0 375 96.77'
            preserveAspectRatio='none'
          >
            <defs>
              <linearGradient
                id='tab-linear-gradient'
                x1='330.5'
                y1='47.65'
                x2='57'
                y2='47.65'
                gradientTransform='matrix(1, 0, 0, -1, 0, 94.53)'
                gradientUnits='userSpaceOnUse'
              >
                <stop
                  offset='0'
                  stopColor='var(--ion-background-color-secondary)'
                />
                <stop offset='0.39' stopColor='var(--ion-color-secondary)' />
                <stop offset='0.7' stopColor='var(--ion-color-secondary)' />
                <stop
                  offset='1'
                  stopColor='var(--ion-background-color-secondary)'
                />
              </linearGradient>
            </defs>
            <g id='Layer_2' data-name='Layer 2'>
              <g id='Layer_1-2' data-name='Layer 1'>
                <path
                  className={classes.tabBgPath1}
                  d='M0,19.53,77.52,17a124.11,124.11,0,0,0,32.34-5.4h0a263.39,263.39,0,0,1,154.75,0h0A122.43,122.43,0,0,0,297.13,17L375,19.27v74.5H0Z'
                />
                <path
                  className={classes.tabBgPath2}
                  d='M0,19.77H75.87a121.62,121.62,0,0,0,35.54-5.34h0a259,259,0,0,1,151.78,0h0a120.21,120.21,0,0,0,35.42,5.34H375v77H0Z'
                />
              </g>
            </g>
          </svg>
          <IonTabs className={classes.tabContainer}>
            <IonTabBar slot='bottom' className={classes.mainTabs}>
              <IonTabButton tab='stories' href='/stories'>
                <StoryIcon />
                <IonLabel>Stories</IonLabel>
              </IonTabButton>
              <IonTabButton tab='workouts' href='/workouts'>
                <WorkoutIcon />
                <IonLabel>Workouts</IonLabel>
              </IonTabButton>
              <IonTabButton tab='dashboard' className={classes.layoutHome}>
                <IonButton
                  fill='clear'
                  ref={homeButtonRef}
                  // onClick={() => setIsBottomDrawerOpen(true)}
                >
                  {logoIconUrl ? (
                    <img
                      src={logoIconUrl}
                      alt='Logo'
                      className={classes.logoIcon}
                    />
                  ) : (
                    <BarwisIcon />
                  )}
                </IonButton>
              </IonTabButton>
              <IonTabButton tab='fueling-plans' href='/fueling-plans'>
                <FuelingPlanIcon />
                <IonLabel>Fuelings</IonLabel>
              </IonTabButton>
              <IonTabButton tab='file-explorer' href='/file-explorer'>
                <FileExplorerIcon />
                <IonLabel>Files</IonLabel>
              </IonTabButton>
            </IonTabBar>
            <IonRouterOutlet id='main'>
              <Redirect exact path='/' to='/dashboard' />
              <Route path='/stories' component={StoryPage} />
              <Route
                path='/reports/readiness-progress'
                exact
                component={ReadinessProgressPage}
              />
              <Route path='/workouts/:id' exact component={WorkoutSinglePage} />
              <Route path='/workouts' exact component={WorkoutsPage} />
              <Route
                path='/workouts/:id/exercise'
                component={ExerciseSinglePage}
              />
              <Route path='/fueling-plans' component={FuelingPlanPage} />
              <Route path='/file-explorer' component={FileExplorerPage} />
              <Route path='/dashboard' component={DashboardPage} />
              <Route
                path='/educational-resources/:id'
                component={EducationalResourceSinglePage}
              />
              <Route
                path='/educational-resources/tag/:tagId'
                exact
                component={EducationalResourceTagPage}
              />
              <Route
                path='/educational-resources'
                exact
                component={EducationalResourcePage}
              />

              <Route path='/services' component={ServicePage} />
              <Route path='/feedback' component={FeedbackPage} />
              <Route path='/help' component={HelpPage} />
            </IonRouterOutlet>
          </IonTabs>
          <a ref={layoutStore.getDownloadRef()} href='' download></a>
          <Menu />
        </Fragment>
      ) : (
        <Auth />
      )}
      <IonToast
        isOpen={Boolean(toastConfig)}
        onDidDismiss={() => layoutStore.setToastConfig(null)}
        message={toastConfig?.message}
        position='bottom'
        duration={3000}
        color={toastConfig?.color}
        buttons={[
          {
            text: 'OK',
            role: 'cancel'
          }
        ]}
      />
      <BottomDrawer
        isOpen={isBottomDrawerOpen}
        close={() => setIsBottomDrawerOpen(false)}
      />
      <IonLoading isOpen={isAuthenticating} message='Authenticating' />
      <IonLoading isOpen={isLoggingOut} message='Logging Out...' />
      <IonLoading isOpen={isFileLoading} message='Please wait...' />
    </Fragment>
  );
};

export default observer(Layout);
